import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { format, formatDistance } from "date-fns";
import * as React from "react";
import { ExecutionDetailPanel, JobSummary } from "./ExecutionMatrix";
import { RenderJob } from "./RenderJobHistory";

export const RenderJobHistoryPresenter: React.FC<{
  jobs: RenderJob[];
  loading: boolean;
  error: string | null;
  jobSummaries: Map<string, JobSummary>;
  onForceRun: () => void;
  isForceRunning: boolean;
  isRefreshing: boolean;
}> = ({ jobs, loading, error, jobSummaries, onForceRun, isForceRunning, isRefreshing }) => {
  const [expandedJobId, setExpandedJobId] = React.useState<string | null>(null);

  if (loading && jobs.length === 0) {
    return (
      <Stack direction="column" spacing={2}>
        <Typography variant="subtitle2">Render Job History</Typography>
        <CircularProgress size={24} />
      </Stack>
    );
  }

  if (error) {
    return (
      <Stack direction="column" spacing={2}>
        <Typography variant="subtitle2">Render Job History</Typography>
        <Alert severity="error">{error}</Alert>
      </Stack>
    );
  }

  return (
    <Stack direction="column" spacing={2} sx={{ width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="subtitle2">Render Job History</Typography>
            <Tooltip
              title="Force Run resets the checksum and runs the job even if data hasn't changed. Useful for testing or ensuring all databases are synchronized."
              placement="top"
            >
              <InfoOutlinedIcon
                fontSize="small"
                sx={{ color: "text.secondary", cursor: "help" }}
              />
            </Tooltip>
            {isRefreshing && (
              <Tooltip title="Refreshing job list..." placement="top">
                <CircularProgress size={16} color="primary" sx={{ ml: 0.5 }} />
              </Tooltip>
            )}
          </Box>
          <Typography variant="body2" color="textSecondary">
            Recent jobs from Render (update-aircraft, movable obstacles, towing
            equipment). Auto-refreshes every 10 seconds.
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={
            isForceRunning ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <PlayArrowIcon />
            )
          }
          onClick={onForceRun}
          disabled={isForceRunning}
          sx={{ minWidth: "140px" }}
        >
          {isForceRunning ? "Running..." : "Force Run"}
        </Button>
      </Box>
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          maxHeight: "600px",
          overflowY: "auto",
        }}
      >
        {/* Header Row - sticky */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "180px 120px 180px 100px 110px",
            p: 1,
            backgroundColor: "grey.100",
            borderBottom: "2px solid",
            borderColor: "divider",
            fontWeight: "bold",
            fontSize: "0.875rem",
            position: "sticky",
            top: 0,
            zIndex: 1,
          }}
        >
          <Box>Job Type</Box>
          <Box>Status</Box>
          <Box>Created</Box>
          <Box>Duration</Box>
          <Box>Dashboard</Box>
        </Box>
        {jobs.map((job) => (
          <Box key={job.id} sx={{ mb: 0 }}>
            <Box
              onClick={() =>
                setExpandedJobId(expandedJobId === job.id ? null : job.id)
              }
              sx={{
                display: "grid",
                gridTemplateColumns: "180px 120px 180px 100px 110px",
                p: 1,
                borderBottom: "1px solid",
                borderColor: "divider",
                cursor: "pointer",
                "&:hover": { backgroundColor: "action.hover" },
                backgroundColor:
                  expandedJobId === job.id ? "action.selected" : "transparent",
              }}
            >
              <Box>
                {job.startCommand.includes("load_aircraft")
                  ? "Update Aircraft"
                  : job.startCommand.includes("load_movable_obstacles")
                  ? "Update Movable Obstacles"
                  : "Update Towing Equipment"}
              </Box>
              <Box>
                <Chip
                  size="small"
                  label={job.status.toUpperCase()}
                  color={
                    job.status === "succeeded"
                      ? "success"
                      : job.status === "failed"
                      ? "error"
                      : job.status === "pending"
                      ? "warning"
                      : job.status === "running"
                      ? "info"
                      : "default"
                  }
                />
              </Box>
              <Tooltip
                title={format(new Date(job.createdAt), "yyyy-MM-dd hh:mm:ss a")}
                placement="top"
              >
                <Box sx={{ cursor: "help" }}>
                  {formatDistance(new Date(job.createdAt), new Date(), {
                    addSuffix: true,
                  })}
                </Box>
              </Tooltip>
              <Box>
                {job.duration_seconds
                  ? (() => {
                      const totalSeconds = Math.floor(job.duration_seconds);
                      const minutes = Math.floor(totalSeconds / 60);
                      const seconds = totalSeconds % 60;
                      return minutes > 0
                        ? `${minutes}m ${seconds}s`
                        : `${seconds}s`;
                    })()
                  : ""}
              </Box>
              <Box>
                <Link
                  href={job.dashboard_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                >
                  View
                  <OpenInNewIcon fontSize="small" />
                </Link>
              </Box>
            </Box>
            {expandedJobId === job.id && (
              <Box
                sx={{
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  backgroundColor: "grey.50",
                }}
              >
                <ExecutionDetailPanel
                  summary={jobSummaries.get(job.id) || null}
                />
              </Box>
            )}
          </Box>
        ))}
      </Box>
    </Stack>
  );
};
